import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { Fragment } from 'react';
import { InlineNotice } from '../common/InlineNotice';
import { useTranslation } from '@hooks/useTranslation';
import type { SpineCancellationTarget } from './spine-cancellation';

const IDENTIFIERS_TOKEN = '%{identifiers}';
const TEST_ID = 'spine-cancellation-notice';

interface SpineCancellationNoticeProps {
  targets: SpineCancellationTarget[];
  /** The linked orders couldn't be read — says so rather than showing nothing. */
  unavailable?: boolean;
  id?: string;
  className?: string;
}

export function SpineCancellationNotice({
  targets,
  unavailable = false,
  id,
  className,
}: SpineCancellationNoticeProps) {
  const { t } = useTranslation();

  // t() echoes the key back on a miss — return null rather than the dot-path.
  const copy = (name: string): string | null => {
    const key = `spine_cancellation.${name}`;
    const value = t(key);
    return value === key ? null : value;
  };

  if (unavailable) {
    const message = copy('unavailable');
    return message === null ? null : (
      <InlineNotice id={id} data-testid={TEST_ID} className={className}>
        {message}
      </InlineNotice>
    );
  }

  if (targets.length === 0) return null;

  // The ids render as links, so split the template rather than interpolate.
  const template = copy(targets.length === 1 ? 'one' : 'other');
  if (template === null || !template.includes(IDENTIFIERS_TOKEN)) return null;

  const [before, after = ''] = template.split(IDENTIFIERS_TOKEN);

  return (
    <InlineNotice id={id} data-testid={TEST_ID} className={className}>
      {before}
      {targets.map((target, index) => (
        <Fragment key={target.identifier}>
          {index > 0 ? ', ' : null}
          {target.adminUrl ? (
            <a href={target.adminUrl} target="_blank" rel="noopener noreferrer" className={UNDERLINE_STATIC}>
              {target.identifier}
            </a>
          ) : (
            target.identifier
          )}
        </Fragment>
      ))}
      {after}
    </InlineNotice>
  );
}
